import { DynamicHoverData } from '../../types/dynamicHover';
import { detectTicketContext } from '../../utils/ticketContextDetector';
import { isCoiRelationships, mapCoiHoverData } from './coiMapper';
import {
  isCoiOtherOccupation,
  mapCoiOtherOccupationHoverData,
} from './coiOtherOccupationMapper';
import {
  isEmployeeMovementsInternalMovement,
  isEmployeeMovementsReferrals,
  isEmployeeMovementsRehiring,
  mapEmployeeMovementsHoverData,
} from './employeeMovementsMapper';
import {
  isGiftsAndDonationsOfficeInternalUsage,
  isGiftsAndDonationsYouAreGiving,
  isGiftsAndDonationsYouAreReceiving,
  mapGiftsAndDonationsHoverData,
} from './giftsAndDonationsMapper';
import {
  isOtherComplianceMatters,
  mapOtherComplianceMattersHoverData,
} from './otherComplianceMattersMapper';

interface Answer {
  question: {
    questionTranslationKey: string;
  };
  answer: string;
  selectedOption?: {
    translationKey: string;
  };
}

interface TicketData {
  answers: Answer[];
  description?: string;
  employee?: {
    employeeId: string;
    band: string;
    positionTitle: string;
  };
  inBehalfOfId?: string;
}

export function detectAndMapDynamicHoverData(
  ticketData: TicketData
): DynamicHoverData | null {
  try {
    const context = detectTicketContext(ticketData.answers);

    if (
      isGiftsAndDonationsYouAreGiving(context) ||
      isGiftsAndDonationsYouAreReceiving(context) ||
      isGiftsAndDonationsOfficeInternalUsage(context)
    ) {
      return mapGiftsAndDonationsHoverData(ticketData.answers, context);
    }

    if (isOtherComplianceMatters(context)) {
      return mapOtherComplianceMattersHoverData(ticketData.answers, context);
    }

    if (isCoiRelationships(context) && ticketData.employee) {
      return mapCoiHoverData(
        ticketData.answers,
        context,
        ticketData.employee.employeeId,
        ticketData.employee.band,
        ticketData.employee.positionTitle,
        ticketData.inBehalfOfId
      );
    }

    if (isCoiOtherOccupation(context) && ticketData.employee) {
      return mapCoiOtherOccupationHoverData(
        ticketData.answers,
        context,
        ticketData.employee.employeeId,
        ticketData.employee.band,
        ticketData.employee.positionTitle,
        ticketData.inBehalfOfId
      );
    }

    if (
      isEmployeeMovementsReferrals(context) ||
      isEmployeeMovementsInternalMovement(context) ||
      isEmployeeMovementsRehiring(context)
    ) {
      return mapEmployeeMovementsHoverData(ticketData.answers, context);
    }

    return null;
  } catch (error) {
    console.warn('Failed to detect dynamic hover data:', error);
    return null;
  }
}

export function shouldUseDynamicHover(ticketData: TicketData): boolean {
  try {
    const context = detectTicketContext(ticketData.answers);
    return (
      isGiftsAndDonationsYouAreGiving(context) ||
      isGiftsAndDonationsYouAreReceiving(context) ||
      isGiftsAndDonationsOfficeInternalUsage(context) ||
      isOtherComplianceMatters(context) ||
      isCoiRelationships(context) ||
      isCoiOtherOccupation(context) ||
      isEmployeeMovementsReferrals(context) ||
      isEmployeeMovementsInternalMovement(context) ||
      isEmployeeMovementsRehiring(context)
    );
  } catch {
    return false;
  }
}

export { type DynamicHoverData } from '../../types/dynamicHover';
